// components/feature-tour/UpdateIntroTour.jsx
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { versionLog } from '../../data/versionLog';

const latestVersionLog = versionLog[0];
const STORAGE_KEY = `hasSeenUpdateIntro_v${latestVersionLog.version}`;

export default function UpdateIntroTour() {
  const [show, setShow] = useState(false);

  // Munculkan tour hanya sekali
  useEffect(() => {
    const hasSeen = localStorage.getItem(STORAGE_KEY);
    if (!hasSeen) {
      const timer = setTimeout(() => {
        setShow(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const closeTour = () => {
    setShow(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  if (!show) return null;

  const content = (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100vh',
            height: '100dvh',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            boxSizing: 'border-box',
            overflowY: 'auto',
          }}
        >
          {/* Backdrop blur */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.65)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
            onClick={closeTour}
          />

          {/* Modal Card - Perfectly Centered */}
          <motion.div
            initial={{ scale: 0.9, y: 16, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.92, y: 10, opacity: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 380 }}
            style={{
              position: 'relative',
              width: 380,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: 'calc(100dvh - 36px)',
              display: 'flex',
              flexDirection: 'column',
              margin: 'auto',
              background: 'rgba(30, 30, 36, 0.88)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderRadius: 24,
              padding: '24px 22px',
              color: 'white',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.14)',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
              boxSizing: 'border-box',
              zIndex: 1,
            }}
          >
            {/* Close button (X) */}
            <button
              onClick={closeTour}
              style={{
                position: 'absolute',
                top: 14,
                right: 14,
                background: 'rgba(255, 255, 255, 0.08)',
                border: 'none',
                fontSize: 20,
                color: '#aaa',
                cursor: 'pointer',
                width: 30,
                height: 30,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
            >
              ×
            </button>

            {/* Judul besar */}
            <h2 style={{
              margin: '4px 28px 8px',
              fontSize: 20,
              fontWeight: 700,
              textAlign: 'center',
              letterSpacing: '-0.3px',
              lineHeight: 1.3,
            }}>
              Update ke V {latestVersionLog.version}!
            </h2>
            <p style={{
              margin: '0 0 18px 0',
              fontSize: 13,
              color: '#cbd5e1',
              textAlign: 'center',
              fontWeight: 400,
              lineHeight: 1.45,
            }}>
              Silahkan klik tombol Update di halaman utama pojok kanan atas untuk menerapkan pembaruan.
            </p>

            {/* List fitur dengan toggle style */}
            <div style={{
              maxHeight: '40vh',
              overflowY: 'auto',
              paddingRight: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}>
              {latestVersionLog.changes.map((change, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 0',
                    borderBottom: i < latestVersionLog.changes.length - 1 ? '1px solid rgba(255, 255, 255, 0.07)' : 'none',
                  }}
                >
                  <div style={{ paddingRight: 12 }}>
                    <div style={{ fontSize: 14, lineHeight: 1.35, fontWeight: 600, textAlign: 'left' }}>
                      {change}
                    </div>
                  </div>

                  {/* Toggle ON (hijau) */}
                  <div style={{
                    width: 46,
                    height: 28,
                    background: '#30d158',
                    borderRadius: 16,
                    position: 'relative',
                    flexShrink: 0,
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.25)',
                  }}>
                    <div style={{
                      position: 'absolute',
                      right: 3,
                      top: 3,
                      width: 22,
                      height: 22,
                      background: 'white',
                      borderRadius: '50%',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.25)',
                    }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Tombol besar di bawah (gaya iOS) */}
            <button
              onClick={closeTour}
              style={{
                marginTop: 20,
                width: '100%',
                background: 'linear-gradient(180deg, #ffffff, #f1f5f9)',
                color: '#0f172a',
                border: 'none',
                padding: '14px',
                borderRadius: 14,
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.01)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              onMouseDown={e => e.currentTarget.style.transform = 'scale(0.98)'}
            >
              Mengerti
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
}
